import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { IBookingFormField, IBusiness, IBusinessEmbedThemeSettings, IService, IServiceCategory, IWorkingDays } from '@core/interfaces';
import { IdentityResult } from '@app/models';

export interface ICreateEmbedTokenRequest {
  businessId: number;
  allowedHost: string;
}

export interface IEmbedToken {
  id: number;
  token: string;
  allowedHost: string;
  createdAt: string;
  embedUrl: string;
}

export interface IUpdateEmbedThemeRequest {
  theme: IBusinessEmbedThemeSettings | null;
}

@Injectable({
  providedIn: 'root'
})
export class BprofileService {

  public businessProfileInfo$: BehaviorSubject<IBusiness | null> = new BehaviorSubject<IBusiness>(null);
  constructor(
    private http: HttpClient
  ) { }

  public getBusinessInfo(): Observable<IBusiness> {
    return this.http.get<IBusiness>(`${environment.apiUrl}api/BusinessProfile/Me`)
      .pipe(tap(profile => this.businessProfileInfo$.next(profile)));
  }

  public updateBusinessInfo(data): Observable<number> {
    return this.http.put<number>(`${environment.apiUrl}api/BusinessProfile/Update`, data);
  }

  public deleteProfile(password: string): Observable<IdentityResult> {
    return this.http.delete<IdentityResult>(`${environment.apiUrl}api/BusinessProfile/DeleteProfile`, {
      params: { password }
    });
  }

  public updateLogo(data: FormData, type: string) {
    return this.http.post(`${environment.apiUrl}api/BusinessProfile/${type}`, data);
  }

  public updateBusinessProfileSubject(data): void {
    this.businessProfileInfo$.next({ ...this.businessProfileInfo$.getValue(), ...data })
  }

  public clearBusinessProfileSubject(): void {
    this.businessProfileInfo$.next(null);
  }

  public removePhoto(type: string) {
    return this.http.delete(`${environment.apiUrl}api/BusinessProfile/${type}`);
  }

  public removeGalleryPhoto(gallery) {
    return this.http.request('delete', `${environment.apiUrl}api/BusinessProfile/GalleryImage`, { body: gallery });
  }

  public getAllCategories(): Observable<IServiceCategory[]> {
    return this.http.get<IServiceCategory[]>(`${environment.apiUrl}api/ServiceCategory/GetAll`);
  }

  public getCategoriesByBusiness(id: number): Observable<IServiceCategory[]> {
    return this.http.get<IServiceCategory[]>(`${environment.apiUrl}api/ServiceCategory/GetAllByBusiness`, { params: { businessId: id } });
  }

  public addServiceCategory(category) {
    return this.http.post(`${environment.apiUrl}api/ServiceCategory/${category.id ? 'Update' : 'Create'}`, category);
  }

  public deleteServiceCategory(category) {
    return this.http.request('delete', `${environment.apiUrl}api/ServiceCategory/Delete`, { body: category });
  }

  public getServices(): Observable<IService[]> {
    return this.http.get<IService[]>(`${environment.apiUrl}api/Service/GetMyServices`);
  }

  public addService(service, action: string) {
    return this.http.post(`${environment.apiUrl}api/Service/${action}`, service);
  }

  public updateService(service) {
    return this.http.post(`${environment.apiUrl}api/Service/Update`, service);
  }

  public deleteService(service) {
    return this.http.request('delete', `${environment.apiUrl}api/Service/Delete`, { body: service });
  }

  public getScheduleConfig(params): Observable<IWorkingDays> {
    return this.http.get<IWorkingDays>(`${environment.apiUrl}api/Calendar/GetScheduleConfig`, { params });
  }

  public setScheduleConfig(payload): Observable<IWorkingDays> {
    return this.http.post<IWorkingDays>(`${environment.apiUrl}api/Calendar/${payload.id === 0 ? 'Create' : 'Update'}`, payload);
  }

  public getEmbedTokens(): Observable<IEmbedToken[]> {
    return this.http.get<IEmbedToken[]>(`${environment.apiUrl}api/BusinessProfile/GetEmbedTokens`);
  }

  public createEmbedToken(payload: ICreateEmbedTokenRequest): Observable<IEmbedToken> {
    return this.http.post<IEmbedToken>(`${environment.apiUrl}api/BusinessProfile/CreateEmbedToken`, payload);
  }

  public revokeEmbedToken(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}api/BusinessProfile/RevokeEmbedToken/${id}`);
  }

  public updateEmbedTheme(theme: IBusinessEmbedThemeSettings | null): Observable<IBusinessEmbedThemeSettings | null> {
    return this.http.put<IBusinessEmbedThemeSettings | null>(`${environment.apiUrl}api/BusinessProfile/UpdateEmbedTheme`, { theme });
  }

  public updateBookingForm(fields: IBookingFormField[]): Observable<IBookingFormField[]> {
    return this.http.put<IBookingFormField[]>(`${environment.apiUrl}api/BusinessProfile/UpdateBookingForm`, { fields });
  }

}
